import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { GridSortModel } from '@mui/x-data-grid';
import clsx from 'clsx';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { Role } from '../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { momentTz } from '../components/subheader/components/DatePicker';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  refreshDurations,
  ToolbarRefreshButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles(() => ({
  noWrap: {
    // whiteSpace: 'nowrap !important' as any,
    // width: 'fit-content !important',
    // blockSize: 'fit-content !important',
  },
  fullWidth: {
    width: '100% !important',
  },
  internalViewOnly: {
    width: '100% !important',
    borderTop: '1px solid #ffec61  !important',
    borderBottom: '1px solid #ffec61  !important',
  },
  internalViewOnlyRow: {
    '& .MuiDataGrid-row:has(.SUPERADMIN)': {
      borderLeft: '3px solid #ffec61  !important',
      borderRight: '3px solid #ffec61  !important',
      borderTop: '1px solid #ffec61  !important',
      borderBottom: '1px solid #ffec61  !important',
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  refreshButtonContainer: {
    marginLeft: 'auto',
  },
}));

const AuditTrail = () => {
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [filter, setFilter] = useState<string>('user');
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents as IPermissions;
  const {
    canViewDebugInformation,
  } = permissionsLoadable.contents as IPermissions;
  const [pageSize, setPageSize] = useState<number>(20);
  const classes = useStyles();
  const [sortModel, _setSortModel] = useState<GridSortModel>([
    { field: 'createDateTime', sort: 'desc' },
  ]);
  const [
    businessTimezoneCached,
    // businessTimezoneLoadable,
  ] = makeApiRequestCached<string | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0]?.timezone_php ??
      cached.current
  );
  const handleSearchChange = useCallback(
    (value: string) => {
      setPage(1);
      setSearch(value);
    },
    [setSearch]
  );
  const payload = useMemo(() => {
    const pl = {
      page: page,
      pageSize: pageSize,
    };
    if (filter && filter !== '' && search && search !== '') {
      pl[`${filter}`] = search;
    }
    return pl;
  }, [page, pageSize, filter, search]);

  const auditTrail = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_AUDIT_TRAIL,
      payloadJsonString: JSON.stringify(payload),
    })
  );

  const setRefetchToggleForAuditTrail = useSetRecoilState(
    refetchToggle(`${methodNameEnum.GET_AUDIT_TRAIL}`)
  );

  const totalCount = useMemo(
    () =>
      auditTrail.state === 'hasValue'
        ? auditTrail?.contents?.data?.totalRows
        : 0,
    [auditTrail]
  );

  const rows = useMemo(
    () =>
      auditTrail?.state === 'loading'
        ? []
        : auditTrail?.contents?.data?.rows
            ?.map((el, i) => ({
              id: page === 1 ? i : pageSize * (page - 1) + i,
              ...el,
              changedBy: el?.userFName + ' ' + el?.userLName,
            }))
            .filter((el) =>
              role === Role.SUPERADMIN ? el : el.internalViewOnly === false
            ), //if superadmin, show all rows, else filter out internal only rows
    [auditTrail, page, pageSize]
  );
  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        search={search}
        setSearch={handleSearchChange}
        isLoading={auditTrail?.state === 'loading'}
        hideVerticalDivider={true}
        leftComponent={
          <div style={{ maxWidth: '175px' }}>
            <FormControl
              style={{ minWidth: '175px' }}
              size="small"
              variant="outlined"
            >
              <InputLabel>Filter Type</InputLabel>
              <Select
                name="type"
                label="Search Filter Type"
                value={filter}
                onChange={(e) => setFilter((e?.target?.value as any) ?? '')}
              >
                <MenuItem value={'user'}>Changed By</MenuItem>
                <MenuItem value={'fieldLabel'}>Field</MenuItem>
                <MenuItem value={'entityType'}>Entity Type</MenuItem>
                <MenuItem value={'entity'}>Entity Name</MenuItem>
                {role === Role.SUPERADMIN && (
                  <MenuItem value={'table'}>Table</MenuItem>
                )}
              </Select>
            </FormControl>
          </div>
        }
        rightComponent={
          <ToolbarRefreshButton
            id="AuditTrail"
            duration={refreshDurations.dur30sec}
            onClickForRefresh={() => {
              setRefetchToggleForAuditTrail({});
            }}
            isLoading={props.isLoading}
          />
        }
      />
    ),
    [auditTrail, search, rows, setFilter, filter]
  );

  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'createDateTime',
        headerName: 'Date',
        headerClassName: classes.fullWidth,
        cellClassName: (params) =>
          params.row.internalViewOnly === '1'
            ? classes.internalViewOnly + ' SUPERADMIN'
            : classes.fullWidth,
        enableSorting: false,
        width: 175,
        valueFormatter: (params: any) =>
          !!params.value?.length
            ? momentTz(
                moment(params?.value).utc(true),
                businessTimezoneCached.current
              )?.format('MM/DD/YYYY h:mm a')
            : '',
      },
      {
        field: 'sourceIPAddress',
        headerName: 'IP Address',
        headerClassName:
          clsx(classes.fullWidth, classes.noWrap) + ' superStyle',
        cellClassName: (params) =>
          params.row.internalViewOnly === '1'
            ? clsx(classes.internalViewOnly, classes.noWrap) + ' SUPERADMIN'
            : clsx(classes.fullWidth, classes.noWrap),
        enableSorting: false,
        width: 120,
        hide: role !== Role.SUPERADMIN,
      },
      {
        field: 'requestedFrom',
        headerName: 'Source',
        headerClassName: classes.fullWidth + ' superStyle',
        cellClassName: (params) =>
          params.row.internalViewOnly === '1'
            ? clsx(classes.internalViewOnly, classes.noWrap) + ' SUPERADMIN'
            : clsx(classes.fullWidth, classes.noWrap),
        enableSorting: false,
        width: 165,
        hide: !canViewDebugInformation, //hide if developer only permission is false (i.e., hide if this user is not a developer)
      },
      {
        field: 'entityType',
        headerName: 'Entity Type',
        headerClassName: clsx(classes.fullWidth, classes.noWrap),
        cellClassName: (params) =>
          params.row.internalViewOnly === '1'
            ? classes.internalViewOnly + ' SUPERADMIN'
            : classes.fullWidth,
        enableSorting: false,
      },
      {
        field: 'entityName',
        headerName: 'Entity Name',
        headerClassName: clsx(classes.fullWidth, classes.noWrap),
        cellClassName: (params) =>
          params.row.internalViewOnly === '1'
            ? classes.internalViewOnly + ' SUPERADMIN'
            : classes.fullWidth,
        enableSorting: false,
      },
      {
        field: 'changedBy',
        headerName: 'Changed By Name',
        headerClassName: clsx(classes.fullWidth, classes.noWrap),
        cellClassName: (params) =>
          params.row.internalViewOnly === '1'
            ? classes.internalViewOnly + ' SUPERADMIN'
            : classes.fullWidth,
        enableSorting: false,
        width: 150,
      },
      {
        field: 'userId',
        headerName: 'ID',
        headerClassName:
          clsx(classes.fullWidth, classes.noWrap) + ' superStyle',
        cellClassName: (params) =>
          params.row.internalViewOnly === '1'
            ? classes.internalViewOnly + ' SUPERADMIN'
            : classes.fullWidth,
        enableSorting: false,
        hide: role !== Role.SUPERADMIN,
      },
      {
        field: 'userEmail',
        headerName: 'Changed By Email',
        headerClassName: clsx(classes.fullWidth, classes.noWrap),
        cellClassName: (params) =>
          params.row.internalViewOnly === '1'
            ? classes.internalViewOnly + ' SUPERADMIN'
            : classes.fullWidth,
        enableSorting: false,
        width: 160,
      },
      {
        field: 'userType',
        headerName: 'Role',
        headerClassName: classes.fullWidth,
        cellClassName: (params) =>
          params.row.internalViewOnly === '1'
            ? classes.internalViewOnly + ' SUPERADMIN'
            : classes.fullWidth,
        enableSorting: false,
      },
      {
        field: 'tableName',
        headerName: 'Table',
        headerClassName: classes.fullWidth + ' superStyle',
        cellClassName: (params) =>
          params.row.internalViewOnly === '1'
            ? classes.internalViewOnly + ' SUPERADMIN'
            : classes.fullWidth,
        enableSorting: false,
        hide: role !== Role.SUPERADMIN,
      },
      {
        field: 'columnName',
        headerName: 'Column',
        headerClassName: classes.fullWidth + ' superStyle',
        cellClassName: (params) =>
          params.row.internalViewOnly === '1'
            ? clsx(classes.internalViewOnly, classes.noWrap) + ' SUPERADMIN'
            : clsx(classes.fullWidth, classes.noWrap),
        enableSorting: false,
        width: 125,
        hide: role !== Role.SUPERADMIN,
      },
      {
        field: 'action',
        headerName: 'Action',
        headerClassName: classes.fullWidth,
        cellClassName: (params) =>
          params.row.internalViewOnly == '1'
            ? classes.internalViewOnly + ' SUPERADMIN'
            : classes.fullWidth,
        enableSorting: false,
      },
      {
        field: 'fieldLabel',
        headerName: 'Field',
        headerClassName: classes.fullWidth,
        cellClassName: (params) =>
          params.row.internalViewOnly == '1'
            ? clsx(classes.internalViewOnly, classes.noWrap) + ' SUPERADMIN'
            : clsx(classes.fullWidth, classes.noWrap),
        enableSorting: false,
        width: 175,
      },
      {
        field: 'oldValue',
        headerName: 'Old Value',
        headerClassName: classes.fullWidth,
        cellClassName: (params) =>
          params.row.internalViewOnly == '1'
            ? classes.internalViewOnly + ' SUPERADMIN'
            : classes.fullWidth,
        enableSorting: false,
        width: 300,
      },
      {
        field: 'newValue',
        headerName: 'New Value',
        headerClassName: classes.fullWidth,
        cellClassName: (params) =>
          params.row.internalViewOnly == '1'
            ? classes.internalViewOnly + ' SUPERADMIN'
            : classes.fullWidth,
        enableSorting: false,
        width: 300,
      },
    ],
    [rows, page, pageSize]
  );

  const handlePageChange = (page: number, details?: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number, details?: any) => {
    setPageSize(pageSize);
  };
  return (
    <div className={classes.internalViewOnlyRow}>
      <DataTable
        rows={rows ?? []}
        columns={columns}
        page={page}
        rowCount={totalCount}
        pageSize={pageSize}
        ToolbarComponent={ToolbarComponent}
        paginationMode="server"
        pagination
        loading={auditTrail?.state === 'loading'}
        sortModel={sortModel}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

export default AuditTrail;
